import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Box } from '@eatclubcode/ec-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { apiCall } from '../../../../actions/actions';
import { enableOfferlessConfigAction } from '../../../../actions/offerlessConfigAction';
import { sendLoyaltyProgramEnquiry } from '../../../../graphql/mutations';
import { trackEvent } from '../../../../utils/analytics';
import { useAppConfig } from '../../../../utils/hooks/useAppConfig';
import { isStaffUser } from '../../../../utils/permissions';
import { DineAnytime } from '../../../DineAnytime/DineAnytime';
import { TextList } from '../../../TextList/TextList';
import { Modal } from '../../../Modal';
import useStyles, { modalInputStyle } from './LoyaltyModalStyles';

const LoyaltyModal = ({ isOpen, onClose, onSave }) => {
  const classes = useStyles();
  const appConfig = useAppConfig();

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const canView = !isStaffUser(user) && !appConfig.loyaltyBlocked; // Don't show for EC staff or in unsupported countries

  // Track when the modal is opened
  useEffect(() => {
    if (isOpen && canView) {
      trackEvent('modal_view: loyalty');
    }
  }, [isOpen, canView]);

  const confirm = () => {
    trackEvent('button_click: accept_loyalty');
    dispatch(enableOfferlessConfigAction());
    onSave();
  };

  // User did not accept the loyalty. Store and ask them again tomorrow
  const handleClose = () => {
    trackEvent('button_click: reject_loyalty');
    onClose();
  };

  const reject = () => {
    apiCall(sendLoyaltyProgramEnquiry, {}, true);
    handleClose();
  };

  // Loyalty is currently only in aus
  if (!canView) {
    return <></>;
  }

  return (
    <Modal
      confirmLabel='Count me in!'
      cancelLabel='Speak to my account manager'
      heading=''
      isOpen={isOpen}
      onClose={handleClose}
      onCancel={reject}
      onConfirm={confirm}
      style={{ content: { padding: '32px' } }}
      inputStyle={modalInputStyle}
      fullWidth
    >
      <Box className={classes.loyaltyModal}>
        <Box className={classes.left}>
          <Box className={classes.label}>IMPORTANT UPDATE</Box>
          <Box className={classes.heading}>
            We’ve improved the way we drive diners to you. Here’s what’s new.
          </Box>

          <TextList
            items={[
              'Customers earn dining credit on retail, petrol, and grocery spend.',
              'Customers earn dining credit at your venue even when no deal is live with offerless transactions.',
              'We charge a standard commission on offerless transactions.',
              'No staff training or operational changes needed.',
              'Opt in today for exclusive access.',
            ]}
          />

          <Box className={classes.section}>
            <Box className={classes.subHeading}>What’s changing</Box>
            <Box>
              <p>
                We’ve introduced a new program where EatClub customers can now earn dining credit on
                everyday purchases like petrol, groceries, and retail shopping at our 600+
                participating partners.
              </p>
              <br />
              <p>
                They can earn 3-10% back in these purchases, which will add up to millions of dining
                credit that can only be spent on redeemed deals at EatClub venues. A new source of
                demand, designed to give diners more to spend the next time they dine at an EatClub
                venue.
              </p>
            </Box>
          </Box>

          <Box className={classes.section}>
            <Box className={classes.subHeading}>Important updates for your venue</Box>
            <TextList
              numbered
              items={[
                {
                  id: 'step_1',
                  value: (
                    <Box style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                      <Box>
                        Customers can use the EatClub digital card at your venue even when you don’t
                        have an offer available.
                      </Box>
                      <DineAnytime />
                    </Box>
                  ),
                },
                'Customers pay full price using the EatClub Card, with no offer applied to the total bill.',
                'We’ll charge our standard commission on these transactions when no deal is live, just like any other EatClub offer redemption today.',
                'Customers can earn dining credit even when no offer is available. EatClub funds the dining credit through our ecosystem. It never comes out of your pocket.',
                'Customers can spend their EatClub Credit exclusively at participating venues. Every dollar earned from groceries, petrol, and retail can only be spent at EatClub restaurants.',
              ]}
              style={{ root: { gap: '16px', paddingTop: '4px' } }}
            />
          </Box>
        </Box>
        <Box className={classes.right}>
          <img
            className={classes.image}
            src={`${appConfig.publicImagePath}/loyalty_2x.png`}
            alt='Two tables. One is using EatClub and earning profit. One is empty and is earning no profit.'
          />
        </Box>
      </Box>
    </Modal>
  );
};

LoyaltyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default LoyaltyModal;
